import getSymbolFromCurrency from 'currency-symbol-map';
import fromExponential from 'from-exponential';

import { CRYPTO_MAX_FRACTION } from 'common/constants/fractions.constants';

export const mapBalanceToCurrency = (amount: number, currency?: string | null): string => {
  const balance = Math.abs(amount || 0).toLocaleString();
  const symbol = getSymbolFromCurrency(currency ?? 'USD');

  return currency ? `${symbol} ${balance}` : balance;
};

export type LocalizeNumberOptions = {
  minDigits?: number;
  maxDigits?: number;
  rounding?: 'up' | 'down' | 'trunc';
  keepSign?: boolean;
  keepNegativeSign?: boolean;
};

export const localizeNumber = (number: number | string | undefined | null, options?: LocalizeNumberOptions) => {
  const minimumFractionDigits =
    options?.minDigits === undefined || options?.minDigits < 0 ? undefined : options?.minDigits;
  const maximumFractionDigits = options?.maxDigits ?? minimumFractionDigits ?? CRYPTO_MAX_FRACTION;

  let targetNumber = Number(number) || 0;
  const precision = targetNumber.toString().split('.')[1] ?? '';

  if (precision.length > maximumFractionDigits) {
    // Rounding behavior
    if (options?.rounding === 'up') {
      targetNumber =
        Math.ceil(targetNumber * Math.pow(10, maximumFractionDigits)) / Math.pow(10, maximumFractionDigits);
    } else if (options?.rounding === 'down') {
      targetNumber =
        Math.floor(targetNumber * Math.pow(10, maximumFractionDigits)) / Math.pow(10, maximumFractionDigits);
    } else {
      // by default `toLocaleString` applies the math rounding, to avoid it we just cut our number by digits. E.g. number: 0.777777, digits: 3 => [0.777, instead of 0.778]
      targetNumber =
        Math.trunc(targetNumber * Math.pow(10, maximumFractionDigits)) / Math.pow(10, maximumFractionDigits);
    }
  }

  if (options?.keepSign && targetNumber >= 0) {
    return `+${fromExponential(
      targetNumber.toLocaleString('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      }),
    )}`;
  }

  if (options?.keepSign && targetNumber < 0) {
    return fromExponential(
      targetNumber.toLocaleString('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      }),
    );
  }

  if (options?.keepNegativeSign)
    return fromExponential(
      targetNumber.toLocaleString('en-US', {
        minimumFractionDigits,
        maximumFractionDigits,
      }),
    );

  return fromExponential(
    Math.abs(targetNumber).toLocaleString('en-US', {
      minimumFractionDigits,
      maximumFractionDigits,
    }),
  );
};
